import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Calendar, X } from "lucide-react";
import {
	addDays,
	addMonths,
	endOfMonth,
	endOfWeek,
	endOfYear,
	format,
	isAfter,
	isBefore,
	isSameDay,
	isSameMonth,
	parseISO,
	startOfDay,
	startOfMonth,
	startOfWeek,
	startOfYear,
	subDays,
} from "date-fns";
import { tr as trLocale, enUS } from "date-fns/locale";
import styles from "./DateRangePicker.module.css";

export interface DateRangePickerProps {
	from: string;
	to: string;
	onChange: (range: { from: string; to: string }) => void;
	placeholder?: string;
	className?: string;
}

const ISO = "yyyy-MM-dd";

function parse(value: string): Date | null {
	if (!value) return null;
	const d = parseISO(value);
	return Number.isNaN(d.getTime()) ? null : d;
}

export function DateRangePicker({
	from,
	to,
	onChange,
	placeholder,
	className,
}: DateRangePickerProps) {
	const { t, i18n } = useTranslation();
	const locale = i18n.language?.startsWith("tr") ? trLocale : enUS;
	const [open, setOpen] = useState(false);
	const [pendingStart, setPendingStart] = useState<Date | null>(null);
	const [hovered, setHovered] = useState<Date | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const popoverRef = useRef<HTMLDivElement>(null);
	const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null);

	const fromDate = parse(from);
	const toDate = parse(to);

	const [viewMonth, setViewMonth] = useState<Date>(() => startOfMonth(fromDate ?? new Date()));

	useEffect(() => {
		if (!open) return;
		const onDocClick = (e: MouseEvent) => {
			const target = e.target as Node;
			if (containerRef.current?.contains(target)) return;
			if (popoverRef.current?.contains(target)) return;
			setOpen(false);
			setPendingStart(null);
		};
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				setOpen(false);
				setPendingStart(null);
			}
		};
		document.addEventListener("mousedown", onDocClick);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onDocClick);
			document.removeEventListener("keydown", onKey);
		};
	}, [open]);

	useEffect(() => {
		if (!open) return;
		function updatePosition() {
			const el = containerRef.current;
			if (!el) return;
			const rect = el.getBoundingClientRect();
			setPopoverPos({ top: rect.bottom + 6, left: rect.left });
		}
		updatePosition();
		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);
		return () => {
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [open]);

	// Panel render genişliği/yüksekliği ölçülüp ekranın sağ/alt kenarını taşıyorsa içeri kaydırılır.
	useLayoutEffect(() => {
		if (!open || !popoverPos) return;
		const el = popoverRef.current;
		if (!el) return;
		const rect = el.getBoundingClientRect();
		const margin = 8;
		let { top, left } = popoverPos;
		const maxLeft = Math.max(margin, window.innerWidth - rect.width - margin);
		if (left > maxLeft) left = maxLeft;
		if (top + rect.height > window.innerHeight - margin) {
			const container = containerRef.current;
			const containerTop = container?.getBoundingClientRect().top ?? top;
			top = Math.max(margin, containerTop - rect.height - 6);
		}
		if (left !== popoverPos.left || top !== popoverPos.top) {
			setPopoverPos({ top, left });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, popoverPos, viewMonth]);

	const presets = useMemo(() => {
		const today = startOfDay(new Date());
		return [
			{ label: t("today"), from: today, to: today },
			{ label: t("last7Days"), from: subDays(today, 6), to: today },
			{ label: t("last30Days"), from: subDays(today, 29), to: today },
			{ label: t("thisMonth"), from: startOfMonth(today), to: endOfMonth(today) },
			{
				label: t("lastMonth"),
				from: startOfMonth(addMonths(today, -1)),
				to: endOfMonth(addMonths(today, -1)),
			},
			{ label: t("thisYear"), from: startOfYear(today), to: endOfYear(today) },
		];
	}, [t]);

	const weeks = useMemo(() => {
		const gridStart = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 });
		const gridEnd = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 1 });
		const days: Date[] = [];
		for (let d = gridStart; !isAfter(d, gridEnd) && days.length < 42; d = addDays(d, 1)) {
			days.push(d);
		}
		const rows: Date[][] = [];
		for (let i = 0; i < days.length; i += 7) rows.push(days.slice(i, i + 7));
		return rows;
	}, [viewMonth]);

	const rangeStart = pendingStart ?? fromDate;
	const rangeEnd = pendingStart ? hovered : toDate;

	const inRange = (day: Date) => {
		if (!rangeStart || !rangeEnd) return false;
		const [a, b] = isAfter(rangeStart, rangeEnd) ? [rangeEnd, rangeStart] : [rangeStart, rangeEnd];
		return !isBefore(day, startOfDay(a)) && !isAfter(day, startOfDay(b));
	};

	const commit = (start: Date, end: Date) => {
		const [a, b] = isAfter(start, end) ? [end, start] : [start, end];
		onChange({ from: format(a, ISO), to: format(b, ISO) });
		setPendingStart(null);
		setHovered(null);
		setOpen(false);
	};

	const handleDayClick = (day: Date) => {
		if (!pendingStart) {
			setPendingStart(day);
			setHovered(day);
			return;
		}
		commit(pendingStart, day);
	};

	const clear = (e: React.MouseEvent) => {
		e.stopPropagation();
		setPendingStart(null);
		setHovered(null);
		onChange({ from: "", to: "" });
	};

	const display = (() => {
		if (!fromDate && !toDate) return placeholder ?? t("selectDateRange");
		const fmt = (d: Date) => format(d, "dd.MM.yyyy");
		if (fromDate && toDate) return `${fmt(fromDate)} – ${fmt(toDate)}`;
		return fromDate ? `${fmt(fromDate)} →` : `→ ${fmt(toDate as Date)}`;
	})();

	const hasValue = Boolean(fromDate || toDate);

	return (
		<div className={`${styles.container} ${className ?? ""}`} ref={containerRef}>
			<button
				type="button"
				className={`${styles.trigger} ${hasValue ? styles.triggerActive : ""}`}
				onClick={() => setOpen((v) => !v)}
			>
				<Calendar size={14} className={styles.triggerIcon} />
				<span className={hasValue ? styles.value : styles.placeholder}>{display}</span>
				{hasValue && (
					<span className={styles.clear} onClick={clear} role="button" aria-label={t("clear")}>
						<X size={13} />
					</span>
				)}
			</button>

			{open &&
				popoverPos &&
				createPortal(
					<div
						ref={popoverRef}
						className={styles.popover}
						style={{ position: "fixed", top: popoverPos.top, left: popoverPos.left }}
					>
						<div className={styles.presets}>
							{presets.map((p) => (
								<button
									key={p.label}
									type="button"
									className={styles.preset}
									onClick={() => commit(p.from, p.to)}
								>
									{p.label}
								</button>
							))}
						</div>

						<div className={styles.calendar}>
							<div className={styles.calendarHeader}>
								<button
									type="button"
									className={styles.navButton}
									onClick={() => setViewMonth(addMonths(viewMonth, -1))}
									aria-label="previous month"
								>
									‹
								</button>
								<span className={styles.monthLabel}>
									{format(viewMonth, "LLLL yyyy", { locale })}
								</span>
								<button
									type="button"
									className={styles.navButton}
									onClick={() => setViewMonth(addMonths(viewMonth, 1))}
									aria-label="next month"
								>
									›
								</button>
							</div>

							<div className={styles.weekdays}>
								{weeks[0]?.map((d) => (
									<span key={d.toISOString()}>{format(d, "EEEEE", { locale })}</span>
								))}
							</div>

							<div onMouseLeave={() => pendingStart && setHovered(pendingStart)}>
								{weeks.map((week) => (
									<div key={week[0]?.toISOString()} className={styles.week}>
										{week.map((day) => {
											const isEdge =
												(rangeStart && isSameDay(day, rangeStart)) ||
												(rangeEnd && isSameDay(day, rangeEnd));
											const classes = [styles.day];
											if (!isSameMonth(day, viewMonth)) classes.push(styles.dayOutside);
											if (inRange(day)) classes.push(styles.dayInRange);
											if (isEdge) classes.push(styles.dayEdge);
											if (isSameDay(day, new Date())) classes.push(styles.dayToday);
											return (
												<button
													key={day.toISOString()}
													type="button"
													className={classes.join(" ")}
													onClick={() => handleDayClick(day)}
													onMouseEnter={() => pendingStart && setHovered(day)}
												>
													{format(day, "d")}
												</button>
											);
										})}
									</div>
								))}
							</div>

							<div className={styles.footer}>
								<span className={styles.hint}>
									{pendingStart ? t("selectEndDate") : t("selectStartDate")}
								</span>
								<button
									type="button"
									className={styles.clearAll}
									onClick={() => {
										setPendingStart(null);
										setHovered(null);
										onChange({ from: "", to: "" });
										setOpen(false);
									}}
								>
									{t("clear")}
								</button>
							</div>
						</div>
					</div>,
					document.body,
				)}
		</div>
	);
}
